// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, defaultDataIdFromObject, Observable } from '@apollo/client';
import { ServerError, ServerParseError } from '@apollo/client/errors';
import { GraphQL17Alpha9Handler } from '@apollo/client/incremental';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { ErrorLink } from '@apollo/client/link/error';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import isExtractableFile from 'apollo-upload-client/isExtractableFile.mjs';
import extractFiles from 'extract-files/extractFiles.mjs';
import { SentryLink } from 'apollo-link-sentry';
import { redirect } from 'jsw';

const sentryLink = new SentryLink({
    setTransaction: false,
});

const errorLink = new ErrorLink(({ error }) => {
    if (!ServerError.is(error) && !ServerParseError.is(error)) {
        return;
    }
    if (error.statusCode === 400) {
        window.location.reload();
        return new Observable(() => {});
    }
    let result;
    try {
        result = JSON.parse(error.bodyText);
    } catch {
        result = null;
    }
    if (result?.forceRedirect) {
        redirect(result.redirect || '/');
        return new Observable(() => {});
    }
});

const headers = {
    'X-Requested-With': 'XMLHttpRequest',
};

const tokenEl = document.getElementById('forgery_protection_token');
if (tokenEl) {
    headers['X-Forgery-Protection-Token'] = tokenEl.content;
}

const fetchParams = {
    uri: '/cp/graphql',
    headers,
};
const terminatingLink = ApolloLink.split(
    // Operations with @stream receive a multipart/mixed response, which only HttpLink can parse
    operation => operation.getContext().multipartStream,
    new HttpLink(fetchParams),
    ApolloLink.split(
        operation => operation.getContext().noBatch || extractFiles(operation, isExtractableFile).files.size > 0,
        new UploadHttpLink(fetchParams),
        new BatchHttpLink({
            ...fetchParams,
            batchMax: 10,
            batchInterval: 10,
        }),
    ),
);

const apolloClient = new ApolloClient({
    link: ApolloLink.from([errorLink, sentryLink, terminatingLink]),
    incrementalHandler: new GraphQL17Alpha9Handler(),
    cache: new InMemoryCache({
        typePolicies: {
            AdditionalKey: {
                keyFields: ['keyNumber'],
            },
            // Wire tool call ids such as call_0 repeat across turns
            CopilotToolCall: {
                keyFields: ['messageId', 'id'],
            },
            Domain: {
                fields: {
                    hosting: {
                        merge: true,
                    },
                    webScriptingSettings: {
                        merge: true,
                    },
                    subscription: {
                        merge: true,
                    },
                },
            },
            Subscription: {
                fields: {
                    permissions: { merge: true },
                },
            },
            ExtensionTab: {
                keyFields: false,
            },
            SiteButton: {
                keyFields: false,
            },
            ConfigSectionGdprCookie: {
                keyFields: ['name'],
            },
            User: {
                keyFields: ['login'],
            },
            StickerCollection: {
                merge: true,
            },
            UserLogo: {
                merge: true,
            },
            UserPermissions: {
                merge: true,
            },
            Os: {
                merge: true,
            },
            Mode: {
                merge: true,
            },
            Home: {
                merge: true,
            },
            Notification: {
                keyFields: [],
            },
            PrimaryKey: {
                keyFields: ['pleskKeyId'],
            },
            WebsitesDiagnostic: {
                keyFields: [],
                fields: {
                    settings: {
                        merge: true,
                    },
                },
            },
            ServerMail: {
                keyFields: [],
                fields: {
                    spamAssassin: {
                        merge: true,
                    },
                },
            },
            Capability: {
                keyFields: [],
                fields: {
                    mailServer: {
                        merge: true,
                    },
                    packages: {
                        merge: true,
                    },
                },
            },
            Dns: {
                keyFields: [],
            },
            Query: {
                fields: {
                    additionalKeys: {
                        merge: false,
                    },
                    recentSearch: {
                        merge: false,
                    },
                    node: {
                        read(_, { args: { id }, toReference }) {
                            const __typename = atob(id).split(':')[0];
                            return toReference({ id, __typename });
                        },
                    },
                },
            },
            Server: {
                keyFields: [],
                fields: {
                    firewall: {
                        merge: true,
                    },
                    components: {
                        merge: false,
                    },
                    settings: {
                        merge: true,
                    },
                    information: {
                        merge: true,
                    },
                    productVersion: {
                        merge: true,
                    },
                    productUpdates: {
                        merge: true,
                    },
                    pum: {
                        merge: true,
                    },
                },
            },
            DnsTemplate: {
                keyFields: [],
            },
            DnsSoaRecordTemplate: {
                keyFields: [],
            },
            TaskList: {
                keyFields: [],
            },
            IpAddress: {
                fields: {
                    clients: {
                        merge: false,
                    },
                },
            },
            IpAddressList: {
                keyFields: [],
                fields: {
                    list: {
                        merge: false,
                    },
                },
            },
            VirtualDirectory: {
                keyFields: ['key'],
                fields: {
                    errorDoc: {
                        read: (_, { args: { code }, toReference }) => toReference({
                            code,
                            __typename: 'VirtualDirectoryErrorDoc',
                        }),
                    },
                },
            },
            VirtualDirectoryErrorDoc: {
                keyFields: ['code'],
            },
            SystemMessage: {
                keyFields: ['key'],
            },
            ClientStatistics: {
                merge: true,
            },
            DomainStatistics: {
                merge: true,
            },
            MailAccountConfiguration: {
                merge: true,
            },
        },
        dataIdFromObject: object => {
            switch (true) {
                case object.__typename?.startsWith('Config'):
                    return object.__typename;
                default:
                    return defaultDataIdFromObject(object);
            }
        },
    }),
});

export default apolloClient;
